import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useLayoutEffect,
} from "react";
import PropTypes from "prop-types";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../firebaseConfig";
import AvatarById from "./common/AvatarById";
import "./MentionAutocomplete.css";

// ─── Firestore prefix search ──────────────────────────────────────────────────

/**
 * Queries the `users` collection for usernames that start with `prefix`.
 * Uses a Firestore range query on `username_lower` which is covered by an
 * existing index (no extra index required).
 *
 * @param {string} prefix  Lower-cased partial username (without the @ sign).
 * @returns {Promise<Array<{ id: string, username: string, photoURL: string, profession: string }>>}
 */
async function searchUsersByPrefix(prefix) {
  if (!prefix || prefix.length < 1) return [];

  const lowerPrefix = prefix.toLowerCase();
  // Firestore range end: replace the last character with its successor so the
  // range covers everything that starts with `lowerPrefix`.
  const rangeEnd =
    lowerPrefix.slice(0, -1) +
    String.fromCharCode(lowerPrefix.charCodeAt(lowerPrefix.length - 1) + 1);

  try {
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("username_lower", ">=", lowerPrefix),
      where("username_lower", "<", rangeEnd),
      limit(5),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      username: d.data().username || d.data().username_lower || "",
      photoURL: d.data().photoURL || d.data().userPhotoURL || "",
      profession: d.data().userProfession || d.data().profession || "",
    }));
  } catch (err) {
    console.error("[MentionAutocomplete] search error:", err);
    return [];
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * MentionAutocomplete
 *
 * A controlled wrapper around a `<textarea>` that intercepts `@username` typing
 * and shows an autocomplete dropdown of matching users.
 *
 * Props mirror native textarea props wherever possible so it can be used as a
 * drop-in replacement:
 *
 *   <MentionAutocomplete
 *     value={text}
 *     onChange={(newValue) => setText(newValue)}
 *     placeholder="Write a comment…"
 *     className="my-textarea"
 *     maxLength={1000}
 *   />
 *
 * The `onChange` prop receives the updated **string** value (not a synthetic
 * event) so it matches the existing `setCommentText` / `setReplyText` pattern
 * already used in this codebase.
 *
 * Keyboard behaviour (when dropdown is open):
 *   ArrowDown / ArrowUp — move selection
 *   Enter               — insert selected username
 *   Escape              — close dropdown
 *   Tab                 — insert selected username and advance focus
 */
const MentionAutocomplete = React.forwardRef(
  (
    {
      value,
      onChange,
      onKeyDown: externalKeyDown,
      onPaste,
      placeholder,
      className,
      rows,
      maxLength,
      disabled,
      autoFocus,
      style,
      "aria-label": ariaLabel,
      onMentionSelect,
      mentionNames = [],
    },
    forwardedRef,
  ) => {
    // ── State ──────────────────────────────────────────────────────────────
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentionState, setMentionState] = useState(null);
    // { triggerIndex: number, query: string }
    // triggerIndex = position of the '@' character in value

    const [isSearching, setIsSearching] = useState(false);

    const textareaRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchTimerRef = useRef(null);
    const latestQueryRef = useRef(""); // prevents stale async results
    // Tracks the most recent cursor (selectionStart) across ALL input events:
    // keystrokes (handleChange) and cursor movements (handleSelect).
    // This avoids reading el.selectionStart inside effects, which can be stale.
    const cursorPosRef = useRef(0);
    // PHASE 4 FIX: Track pending paste for cursor race condition fix (FINDING-UI-1)
    const pastePendingRef = useRef(null);
    // Bumped by handleSelect every time the cursor moves without a value change.
    // Adding this to the mention-detection effect's dependency array causes it to
    // re-run on click / arrow-key repositioning, fixing dropdown dismissal.
    const [cursorVersion, setCursorVersion] = useState(0);

    // Expose the inner textarea ref to callers who pass a forwardedRef
    useImperativeHandle(forwardedRef, () => textareaRef.current, []);

    // The highlighter sits behind the textarea (which has transparent text)
    // and renders structured tokens as blue @mention spans.
    const highlighterContent = useMemo(() => {
      if (!value) return null;

      if (!mentionNames || mentionNames.length === 0) {
        return <span>{value}</span>;
      }

      // Sort names by length descending to match longest first (e.g. "Omid Khan" before "Omid")
      const sortedNames = [...mentionNames].sort((a, b) => b.length - a.length);
      const escapedNames = sortedNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      
      // Create a regex that matches exactly @ followed by any of the known names
      // using capturing group so we can split the text and keep the matches
      const tokenRegex = new RegExp(`(@(?:${escapedNames.join('|')}))`, 'g');
      
      const parts = String(value).split(tokenRegex);

      return parts.map((part, i) => {
        if (!part) return null;
        
        // If this part is one of our matches, it starts with '@' and is exactly a known name
        if (part.startsWith('@') && sortedNames.includes(part.substring(1))) {
          return (
            <span key={`m-${i}`} className="mention-highlighter__mention">
              {part}
            </span>
          );
        }
        
        // Regular text
        return <span key={`t-${i}`}>{part}</span>;
      });
    }, [value, mentionNames]);

    // ── Scroll + style sync: highlighter matches textarea exactly ──
    const highlighterRef = useRef(null);
    const scrollRafRef = useRef(null);

    const handleTextareaScroll = useCallback(() => {
      // Throttle to rAF — prevents desync during fast scroll
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        if (highlighterRef.current && textareaRef.current) {
          highlighterRef.current.scrollTop = textareaRef.current.scrollTop;
          highlighterRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
      });
    }, []);

    // PHASE 4 FIX: Cleanup scrollRafRef on unmount to prevent memory leaks (FINDING-UI-2)
    useEffect(() => {
      return () => {
        if (scrollRafRef.current !== null) {
          cancelAnimationFrame(scrollRafRef.current);
          scrollRafRef.current = null;
        }
      };
    }, []);

    // Sync highlighter styles to textarea computed styles for pixel-perfect alignment
    useEffect(() => {
      const textarea = textareaRef.current;
      const hl = highlighterRef.current;
      if (!textarea || !hl) return;

      const syncStyles = () => {
        const cs = window.getComputedStyle(textarea);
        // Sync font properties for alignment
        hl.style.fontFamily = cs.fontFamily;
        hl.style.fontSize = cs.fontSize;
        hl.style.fontWeight = cs.fontWeight;
        hl.style.lineHeight = cs.lineHeight;
        hl.style.letterSpacing = cs.letterSpacing;
        hl.style.wordSpacing = cs.wordSpacing;
        hl.style.textIndent = cs.textIndent;
        hl.style.tabSize = cs.tabSize;
        hl.style.webkitFontSmoothing = cs.webkitFontSmoothing;
        hl.style.mozOsxFontSmoothing = cs.mozOsxFontSmoothing;
        // CRITICAL: Sync padding for cursor alignment
        hl.style.padding = cs.padding;
      };

      syncStyles();
      const observer = new ResizeObserver(syncStyles);
      observer.observe(textarea);
      return () => observer.disconnect();
    }, []);

    // ── Detect @mention as user types or repositions cursor ────────────────────
    // FIX HIGH-1: Previous version only depended on `value`. That meant clicking
    // or arrowing the cursor away from an @ token never re-evaluated the match,
    // leaving the dropdown open at the wrong position.
    // Now depends on `cursorVersion` (incremented by handleSelect on every cursor
    // move) so any repositioning triggers a fresh match against cursorPosRef.
    useEffect(() => {
      // cursorPosRef is kept current by handleChange + handleSelect —
      // no stale DOM read needed here.
      const cursorPos = cursorPosRef.current;
      const textBeforeCursor = (value || "").substring(0, cursorPos);

      // Look for the nearest unbroken @word sequence before the cursor
      const match = textBeforeCursor.match(/@([a-zA-Z0-9_]*)$/);

      if (match) {
        setMentionState({
          triggerIndex: match.index,
          query: match[1],
        });
      } else {
        setMentionState(null);
        setSuggestions([]);
      }
    }, [value, cursorVersion]);

    // ── Debounced Firestore search ────────────────────────────────────────
    useEffect(() => {
      if (!mentionState) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      const { query: partialUsername } = mentionState;

      if (partialUsername.length === 0) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      clearTimeout(searchTimerRef.current);
      setIsSearching(true);
      latestQueryRef.current = partialUsername;

      searchTimerRef.current = setTimeout(async () => {
        const results = await searchUsersByPrefix(partialUsername);
        // Ignore stale results from earlier keystrokes
        if (latestQueryRef.current !== partialUsername) return;
        setSuggestions(results);
        setSelectedIndex(0);
        setIsSearching(false);
      }, 300);

      return () => clearTimeout(searchTimerRef.current);
    }, [mentionState]);

    // ── Insert a selected username into the textarea ──────────────────────
    const insertMention = useCallback(
      (user) => {
        if (!mentionState) return;

        const { triggerIndex } = mentionState;
        const el = textareaRef.current;
        const cursorPos = el?.selectionStart ?? value.length;

        // Option A: Insert only the clean username to preserve caret alignment
        const before = value.substring(0, triggerIndex);
        const after = value.substring(cursorPos);
        const insertion = `@${user.username} `;
        const newValue = before + insertion + after;

        onChange(newValue);
        if (onMentionSelect) {
          onMentionSelect(user);
        }

        // Move cursor to after the inserted mention
        const newCursorPos = triggerIndex + insertion.length;
        requestAnimationFrame(() => {
          if (el) {
            el.focus();
            el.setSelectionRange(newCursorPos, newCursorPos);
            // Reset auto-grow height
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
          }
        });

        setMentionState(null);
        setSuggestions([]);
        setSelectedIndex(0);
      },
      [mentionState, onChange, value],
    );

    // ── Keyboard navigation ───────────────────────────────────────────────
    const handleKeyDown = useCallback(
      (e) => {
        if (suggestions.length > 0) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1));
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === "Enter" || e.key === "Tab") {
            if (suggestions[selectedIndex]) {
              e.preventDefault();
              insertMention(suggestions[selectedIndex]);
              return;
            }
          }
          if (e.key === "Escape") {
            e.preventDefault();
            setMentionState(null);
            setSuggestions([]);
            return;
          }
        }

        // Pass through to any external handler (e.g. Enter-to-submit)
        externalKeyDown?.(e);
      },
      [suggestions, selectedIndex, insertMention, externalKeyDown],
    );

    // ── Auto-grow textarea height + cursor tracking ───────────────────────────
    const handleChange = useCallback(
      (e) => {
        const el = e.target;
        // Record cursor on every keystroke so mention detection always has
        // the true insertion point without reading stale DOM state at effect time.
        cursorPosRef.current = el.selectionStart ?? 0;
        onChange(el.value);
      },
      [onChange],
    );

    // ── Cursor-position tracking on click / arrow / Home / End ─────────────
    // FIX HIGH-1: onSelect fires on every selection change even when `value`
    // does not change. Incrementing cursorVersion makes the mention-detection
    // effect re-run, dismissing dropdown when the cursor moves away from an
    // @ token (e.g. user clicks somewhere else inside the textarea).
    const handleSelect = useCallback((e) => {
      cursorPosRef.current = e.target.selectionStart ?? 0;
      setCursorVersion((v) => v + 1);
    }, []);

    // ── Centralized paste sanitizer (fixes CRIT-1 + CRIT-2) ────────────────
    // CRIT-1: CommentBox.js / ReplyBox.js / ReplyItem.jsx each had their own
    //   handlePaste that called setState(newValue) then setTimeout(0) to
    //   restore the cursor. setTimeout(0) races with React’s commit phase and
    //   causes the cursor to jump to end on any non-trivial device.
    // CRIT-2: Comment-edit and reply-edit <MentionAutocomplete> had NO paste
    //   handler at all, so pasting rich text inserted raw HTML entities / \r\n.
    //
    // Fix: one internal handler for every MentionAutocomplete instance.
    //   • Uses el.value (live DOM value), not stale React state.
    //   • PHASE 4 FIX: Uses ref-based tracking + effect for cursor positioning
    //     to avoid race conditions with React's commit phase (FINDING-UI-1).
    const handleInternalPaste = useCallback(
      (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text");
        // Normalize \r\n (Windows) and bare \r (classic Mac) → \n
        // eslint-disable-next-line no-control-regex
        const clean = pasted.replace(/\r\n?|\u000d\u000a/g, "\n");

        const el = e.target;
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;
        // el.value is the live DOM value — avoids using stale React state
        const current = el.value;
        const newValue =
          current.substring(0, start) + clean + current.substring(end);

        // Enforce maxLength before calling onChange
        const capped =
          maxLength != null ? newValue.slice(0, maxLength) : newValue;
        const newCursor = Math.min(start + clean.length, capped.length);

        // Track cursor before React re-renders so mention detection is correct
        cursorPosRef.current = newCursor;

        // PHASE 4 FIX: Store paste info for effect-based cursor positioning
        pastePendingRef.current = {
          cursor: newCursor,
          timestamp: Date.now(),
        };

        onChange(capped);

        // Notify external listener AFTER internal handling is done.
        // Consumers must NOT call e.preventDefault() here (already handled).
        onPaste?.(e, capped);
      },
      [onChange, onPaste, maxLength],
    );

    // FIX: Using useLayoutEffect to synchronously restore cursor position after render
    // to prevent the caret from jumping due to React controlled-component re-renders.
    useLayoutEffect(() => {
      if (!textareaRef.current) return;
      const el = textareaRef.current;
      
      // Sync auto-resize
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;

      // Restore cursor position if there was a pending paste
      if (pastePendingRef.current) {
        const { cursor } = pastePendingRef.current;
        el.setSelectionRange(cursor, cursor);
        pastePendingRef.current = null;
      }
    }, [value]);

    // ── Close on outside click ────────────────────────────────────────────
    useEffect(() => {
      if (suggestions.length === 0) return;

      const handleOutsideClick = (e) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target) &&
          textareaRef.current &&
          !textareaRef.current.contains(e.target)
        ) {
          setSuggestions([]);
          setMentionState(null);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }, [suggestions.length]);

    // ── Render ────────────────────────────────────────────────────────────
    const showDropdown = suggestions.length > 0;

    return (
      <div className={`mention-autocomplete-wrapper ${className || ""}`}>
        {/* Highlighter — behind textarea, renders styled mentions */}
        {value && (
          <div
            ref={highlighterRef}
            className="mention-highlighter"
            aria-hidden="true"
          >
            {highlighterContent}
          </div>
        )}

        {/* Textarea — transparent text/border/bg, visible caret, captures input */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onScroll={handleTextareaScroll}
          onKeyDown={handleKeyDown}
          onSelect={handleSelect}
          onPaste={handleInternalPaste}
          placeholder={placeholder}
          className="mention-autocomplete__textarea"
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          style={{
            ...style,
            // Issue 3 Fix: Force transparency via inline style to override any CSS conflicts
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            background: 'transparent',
            caretColor: '#1a1a1a',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
          }}
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="mention-suggestion-listbox"
          aria-activedescendant={
            showDropdown && suggestions[selectedIndex]
              ? `mention-option-${selectedIndex}`
              : undefined
          }
        />

        {/* Loading indicator — only shown during the 300ms debounce + fetch.
            role="status" + aria-live="polite" announces "Searching…" to screen
            readers without interrupting the current reading flow. */}
        {isSearching && mentionState && mentionState.query.length > 0 && (
          <div
            className="mention-dropdown mention-dropdown--loading"
            role="status"
            aria-live="polite"
          >
            <span className="mention-loading-text">Searching…</span>
          </div>
        )}

        {/* Suggestions dropdown — id must match aria-controls on the textarea
            so assistive technology can announce the expanded listbox correctly. */}
        {showDropdown && (
          <div
            ref={dropdownRef}
            id="mention-suggestion-listbox"
            className="mention-dropdown"
            role="listbox"
            aria-label="Mention suggestions"
          >
            {suggestions.map((user, idx) => (
              <div
                key={user.id}
                id={`mention-option-${idx}`}
                className={`mention-option${idx === selectedIndex ? " mention-option--selected" : ""}`}
                role="option"
                aria-selected={idx === selectedIndex}
                onMouseDown={(e) => {
                  // mousedown fires before blur, preventing textarea from losing focus
                  e.preventDefault();
                  insertMention(user);
                }}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <AvatarById
                  userId={user.id}
                  fallbackUsername={user.username}
                  size="small"
                  className="mention-option__avatar"
                />
                <div className="mention-option__info">
                  <span className="mention-option__username">
                    @{user.username}
                  </span>
                  {user.profession && (
                    <span className="mention-option__profession">
                      {user.profession}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

MentionAutocomplete.displayName = "MentionAutocomplete";

MentionAutocomplete.propTypes = {
  /** Controlled textarea value — must be a plain string. */
  value: PropTypes.string.isRequired,
  /** Called with the new string value (not a synthetic event). */
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  style: PropTypes.object,
  "aria-label": PropTypes.string,
  onMentionSelect: PropTypes.func,
  mentionNames: PropTypes.arrayOf(PropTypes.string),
};

export default MentionAutocomplete;
